import moment from 'moment-timezone';
import { RemoveUnderscoreInstring } from '../RemoveUnderscoreInString/RemoveUnderscoreInString';
import { DEFAULT_TIMEZONE } from 'utils/timezone';

export const DateFormatOnly = (dateString) => {
    // Check if dateString is null
    if (dateString === null) {
        return '';
    }

    // Parse the input date string using Moment.js
    const date = moment.utc(dateString).local();

    // Format the date in the "16 Jan 2024" format
    const formattedDate = date.format('D MMM YYYY');

    return formattedDate;
};

const parseFormattedDate = (dateString) => {
    let date = moment.tz(dateString, 'D MMM YYYY hh:mm A', DEFAULT_TIMEZONE);
    if (!date.isValid()) {
        date = moment.tz(dateString, 'D MMM YYYY h:mm A', DEFAULT_TIMEZONE);
    }
    if (!date.isValid()) {
        date = moment.tz(dateString, 'DD MMM YYYY hh:mm A', DEFAULT_TIMEZONE);
    }
    if (!date.isValid()) {
        date = moment(dateString, 'D MMM YYYY hh:mm A').tz(DEFAULT_TIMEZONE);
    }
    return date;
};

const parseUnknownDate = (dateString) => {
    let date = moment.utc(dateString).tz(DEFAULT_TIMEZONE);
    if (!date.isValid()) {
        date = moment.tz(dateString, DEFAULT_TIMEZONE);
    }
    return date;
};

export const DateFormatWithTime = (dateString) => {
    if (dateString === null || dateString === undefined) {
        return '';
    }

    const isISOFormat = /^\d{4}-\d{2}-\d{2}/.test(dateString) || /^\d{4}-\d{2}-\d{2}T/.test(dateString);
    const isFormattedDate =
        (!isISOFormat && /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i.test(dateString)) ||
        /\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}/i.test(dateString);

    let date;
    if (isISOFormat) {
        date = moment.utc(dateString).tz(DEFAULT_TIMEZONE);
    } else if (isFormattedDate) {
        date = parseFormattedDate(dateString);
    } else {
        date = parseUnknownDate(dateString);
    }

    if (!date.isValid()) {
        date = moment.tz(DEFAULT_TIMEZONE);
    }

    return date.format('D MMM YYYY hh:mm A');
};

export const apiTimeStamp = (dateString) => {
    // Check if dateString is null
    if (dateString === null) {
        return '';
    }

    // Parse the input date string using Moment.js with the specified format
    const date = moment.utc(dateString, 'YYYY-MM-DD-HH:mm:ss.SSS');

    // Format the date in the "16 Jan 2024" format
    const formattedDate = date.format('D MMM YYYY');

    return formattedDate;
};

const PropertyFormat = (value, transformFunction = (v) => v, defaultValue = '-') => {
    // Check if value is undefined, null, empty string, or equal to defaultValue
    if (value === undefined || value === null || value === '' || (value === defaultValue && value !== 0)) {
        return defaultValue;
    }

    if (typeof transformFunction === 'function') {
        return transformFunction(value);
    }

    return value;
};

const toNumber = (input) => {
    if (typeof input === 'number') {
        return Number.isNaN(input) ? null : input;
    }

    if (typeof input === 'string') {
        const sanitized = input.replace(/[^0-9.+-]/g, '').trim();

        if (sanitized === '' || sanitized === '+' || sanitized === '-' || sanitized === '.') {
            return 0;
        }

        if (sanitized.includes('+')) {
            return sanitized.split('+').reduce((sum, part) => {
                const num = parseFloat(part);
                return Number.isNaN(num) ? sum : sum + num;
            }, 0);
        }

        const numeric = parseFloat(sanitized);
        return Number.isNaN(numeric) ? null : numeric;
    }

    return null;
};

export const amountFormat = (amount) => {
    if (amount === null || amount === undefined) {
        return '$0.00';
    }

    const normalizedValue = toNumber(amount);

    if (normalizedValue === null) {
        return amount;
    }

    const formattedValue = normalizedValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return `$${formattedValue}`;
};

export const calculateInMillions = (value) => {
    // Check if value is a valid number
    if (typeof value === 'number') {
        // Conditionally format based on the value
        if (value >= 1000000) {
            // Convert the amount to millions
            const amountInMillions = value / 1000000;
            // Return the formatted amount in millions
            return `$${amountInMillions.toFixed(2)} M`;
        } else if (value > 1000 && value < 1000000) {
            // Convert the amount to thousands
            const amountInThousands = value / 1000;
            // Return the formatted amount in thousands
            return `$${amountInThousands.toFixed(2)} K`;
        } else if (value !== 0) {
            // Return the original value for amounts other than 0
            return `$${value.toFixed(2)}`;
        } else {
            // Return 0 for the value of 0
            return '$0';
        }
    }

    return value;
};

export const getLastValueFromFirstunderscore = (inputString) => {
    if (!inputString && typeof inputString !== 'string') {
        return '';
    }
    let parts = inputString.split('.');

    if (parts?.length > 0) {
        parts = parts.slice(-1).pop();
    }

    return RemoveUnderscoreInstring(parts);
};

export const getAllLastValueFromFirstunderscore = (inputString) => {
    if (!inputString && typeof inputString !== 'string') {
        return '';
    }
    let result;
    result = inputString.includes('-') && inputString.split('-').slice(1).join('-').trim();
    return result;
};

export const formatDateAsPerResponse = (dateString) => {
    if (!dateString) return ''; // Handle case when dateString is null or undefined

    // Check if the date is in the "yyyy-mm-dd" format
    const isYYYYMMDDFormat = /^\d{4}-\d{2}-\d{2}$/.test(dateString);

    if (isYYYYMMDDFormat) {
        return dateString; // Return as is if already in "yyyy-mm-dd" format
    }

    // Convert the date to a Date object
    const date = new Date(dateString);

    // Check if the date is valid
    if (isNaN(date.getTime())) return ''; // Invalid date, return an empty string

    // Format the date to "yyyy-mm-dd"
    const formattedDate = date.toLocaleDateString('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });

    return formattedDate;
};

export const RemoveUnderscoreWithUppercase = (inputString) => {
    // Check if the input is valid
    if (!inputString && typeof inputString !== 'string') {
        return '';
    }
    if (inputString === '3_days') {
        return 'COD';
    }
    if (inputString.startsWith('termsheet_other_fees')) {
        return inputString.replace('termsheet_other_fees', 'Other Fees');
    }
    if (inputString.startsWith('termsheet_handling_fees')) {
        return inputString.replace('termsheet_handling_fees', 'Handling Fees');
    }
    // Split the string into words based on underscores
    const words = inputString?.split('_');

    // Capitalize the first letter of each word and join them with spaces
    const formattedString = words
        .map((word) => {
            if (word.toLowerCase() === 'rfq') {
                return 'RFQ';
            } else if (word.toLowerCase() === 'cod') {
                return 'COD';
            }
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');

    return formattedString;
};
// Function to format the date for the input
export const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const createSyntheticEvent = (name, value) => {
    return {
        target: {
            name,
            value,
        },
    };
};

export function debounceAsync(func, waitMs) {
    let timeout = null;

    return (...args) => {
        // Clear the previous timeout
        clearTimeout(timeout);

        return new Promise((resolve, reject) => {
            timeout = setTimeout(async () => {
                try {
                    // Execute the debounced function
                    const result = await func(...args);
                    resolve(result);
                } catch (error) {
                    reject(error);
                }
            }, waitMs);
        });
    };
}

export function isValidPhoneNumber(phoneNumber) {
    let val;
    const regex = /^\+\d{1,3}$/;
    if (typeof phoneNumber !== 'string' || phoneNumber === undefined || phoneNumber === null) {
        const isNotVaid = regex.test(phoneNumber);

        if (isNotVaid) {
            val = '';
        } else {
            val = phoneNumber;
        }
    }

    return val;
}

export function createFilteredArray(data, addKey, editKey, deleteKey, uniqueKey) {
    if (!data) {
        return [];
    }
    if (Array.isArray(data)) {
        return data;
    }
    const addEntries = Object.values(data[addKey] || {});
    const editEntries = Object.values(data[editKey] || {});
    const deleteEntries = Object.values(data[deleteKey] || {});

    const combinedEntries = [...addEntries, ...editEntries];

    const filteredEntries = combinedEntries.filter(
        (entry) => !deleteEntries.some((deleted) => deleted[uniqueKey] === entry[uniqueKey])
    );

    return filteredEntries.filter(
        (entry, index, self) => index === self.findIndex((e) => e[uniqueKey] === entry[uniqueKey])
    );
}

export default PropertyFormat;
